import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Tabs, message,
  Select, Space, Descriptions, Spin
} from 'antd';
import {
  CheckCircleOutlined, EyeOutlined,
  DownloadOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  collection, getDocs, doc, updateDoc, getDoc,
  query, orderBy, serverTimestamp, addDoc
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { TabPane } = Tabs;
const { Option } = Select;

const STATUS_STYLES = {
  pending: { color: '#FAAD14', badge: 'warning', label: 'Pending' },
  processing: { color: '#1890FF', badge: 'processing', label: 'Processing' },
  completed: { color: '#52C41A', badge: 'success', label: 'Completed' },
  failed: { color: '#FF4D4F', badge: 'error', label: 'Failed' },
};

const GMV_DATA = [
  { month: 'Dec', gmv: 0, commission: 0 },
  { month: 'Jan', gmv: 0, commission: 0 },
  { month: 'Feb', gmv: 0, commission: 0 },
  { month: 'Mar', gmv: 0, commission: 0 },
  { month: 'Apr', gmv: 0, commission: 0 },
  { month: 'May', gmv: 16500, commission: 1582 },
];

export default function FinanceDashboard() {
  const [payouts, setPayouts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [payoutsSnap, ordersSnap] = await Promise.all([
        getDocs(query(collection(db, 'payouts'), orderBy('dueDate', 'desc'))),
        getDocs(collection(db, 'orders')),
      ]);
      setPayouts(payoutsSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        dueDate: d.data().dueDate?.toDate?.()?.toLocaleDateString('en-IN') || 'N/A',
        paidAt: d.data().processedAt?.toDate?.()?.toLocaleDateString('en-IN') || null,
      })));
      setOrders(ordersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      message.error('Failed to load finance data');
    }
    setLoading(false);
  };

  const totalGMV = orders.reduce((sum, o) => sum + (o.orderValue || 0), 0);
  const totalCommission = orders.reduce((sum, o) => sum + (o.commissionAmount || 0), 0);
  const pendingPayout = payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.netPayout || 0), 0);
  const completedPayout = payouts.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.netPayout || 0), 0);

  const filtered = filterStatus === 'all' ? payouts : payouts.filter(p => p.status === filterStatus);

  const handlePayout = async () => {
    setActionLoading(true);
    try {
      const txnRef = 'TXN' + Date.now();
      await updateDoc(doc(db, 'payouts', selected.id), {
        status: 'completed',
        txnRef,
        processedAt: serverTimestamp(),
        processedBy: 'nksapcolouracademy@gmail.com',
      });
      await addDoc(collection(db, 'auditLogs'), {
        action: 'PAYOUT PROCESSED',
        details: 'Dealer: ' + selected.recipientName + ' | Amount: Rs.' + selected.netPayout + ' | TXN: ' + txnRef,
        adminEmail: 'nksapcolouracademy@gmail.com',
        targetId: selected.id, targetType: 'payout',
        timestamp: serverTimestamp(),
      });
      setPayouts(prev => prev.map(p =>
        p.id === selected.id ? { ...p, status: 'completed', txnRef, paidAt: new Date().toLocaleDateString('en-IN') } : p
      ));
      message.success('Payout of Rs.' + selected.netPayout + ' processed — saved to Firebase');
      setConfirmModal(false); setViewModal(false);
    } catch (error) {
      message.error('Payout failed');
    }
    setActionLoading(false);
  };

  const columns = [
    {
      title: 'Dealer',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.recipientName}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.orderCount} orders</div>
        </div>
      ),
    },
    {
      title: 'Order Value',
      render: (_, r) => <span style={{ color: '#D4DCE3', fontWeight: 500 }}>Rs.{r.orderValue?.toLocaleString()}</span>,
    },
    {
      title: 'Commission',
      render: (_, r) => (
        <div>
          <div style={{ color: '#C9A84C', fontWeight: 500 }}>Rs.{r.commissionAmount?.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#7A8A96' }}>+GST Rs.{r.gstOnCommission}</div>
        </div>
      ),
    },
    {
      title: 'Net Payout',
      render: (_, r) => (
        <div>
          <div style={{ color: '#52C41A', fontWeight: 600, fontSize: 15 }}>Rs.{r.netPayout?.toLocaleString()}</div>
          {r.requiresSuperAdmin && (
            <Tag style={{ fontSize: 10, background: 'rgba(250,173,20,0.1)', color: '#FAAD14', border: 'none', borderRadius: 20 }}>
              Super Admin required
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      render: (d) => <span style={{ color: '#7A8A96', fontSize: 12 }}>{d}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s) => {
        const st = STATUS_STYLES[s] || STATUS_STYLES.pending;
        return <Badge status={st.badge} text={<span style={{ color: st.color, fontSize: 12 }}>{st.label}</span>} />;
      },
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} />
          {r.status === 'pending' && (
            <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
              onClick={() => { setSelected(r); setConfirmModal(true); }}>
              Pay
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total GMV', value: totalGMV, prefix: 'Rs.', color: '#C9A84C', desc: 'From orders' },
          { title: 'Commission Earned', value: totalCommission, prefix: 'Rs.', color: '#52C41A', desc: 'KamSathi revenue' },
          { title: 'Pending Payouts', value: pendingPayout, prefix: 'Rs.', color: '#FAAD14', desc: 'To be settled' },
          { title: 'Completed Payouts', value: completedPayout, prefix: 'Rs.', color: '#1890FF', desc: 'Settled' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: '#7A8A96', marginBottom: 4 }}>{stat.title}</div>
              <div style={{ fontSize: 24, fontWeight: 600, color: stat.color }}>
                {stat.prefix}{stat.value?.toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: '#4A4A47', marginTop: 4 }}>{stat.desc}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Tabs defaultActiveKey="payouts" tabBarStyle={{ borderBottom: '0.5px solid rgba(201,168,76,0.2)' }}>
        <TabPane tab="Payout Queue" key="payouts">
          <Card style={{ ...CARD_STYLE, marginBottom: 16, marginTop: 16 }} bodyStyle={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 160 }}>
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="processing">Processing</Option>
                <Option value="completed">Completed</Option>
              </Select>
              <Button type="text" icon={<ReloadOutlined />} style={{ color: '#C9A84C' }} onClick={loadData}>Refresh</Button>
              <Button icon={<DownloadOutlined />} style={{ color: '#C9A84C', borderColor: 'rgba(201,168,76,0.3)' }}>
                Export Excel
              </Button>
              <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
                {loading ? 'Loading...' : `${filtered.length} payouts`}
              </span>
            </div>
          </Card>
          <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
            <Spin spinning={loading}>
              <Table dataSource={filtered} columns={columns} rowKey="id" pagination={{ pageSize: 10 }} />
            </Spin>
          </Card>
        </TabPane>

        <TabPane tab="GMV Analytics" key="gmv">
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={16}>
              <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
                <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 16 }}>Monthly GMV vs Commission</div>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={GMV_DATA}>
                    <defs>
                      <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#C9A84C" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#C9A84C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#7A8A96" />
                    <YAxis stroke="#7A8A96" />
                    <Tooltip contentStyle={{ background: '#1A1A1A', border: '0.5px solid rgba(201,168,76,0.3)', borderRadius: 8 }} />
                    <Area type="monotone" dataKey="gmv" stroke="#C9A84C" fill="url(#gmvGrad)" name="GMV (Rs.)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
                <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 16 }}>Revenue Breakdown</div>
                {[
                  { label: 'Product Orders', amount: Math.round(totalCommission * 0.7), color: '#C9A84C' },
                  { label: 'Lead Sales', amount: 5881, color: '#1890FF' },
                  { label: 'B2B Orders', amount: Math.round(totalCommission * 0.15), color: '#52C41A' },
                  { label: 'Logistics', amount: Math.round(totalCommission * 0.05), color: '#722ED1' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '0.5px solid rgba(201,168,76,0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                      <span style={{ color: '#B0B8C1', fontSize: 13 }}>{item.label}</span>
                    </div>
                    <span style={{ color: item.color, fontWeight: 500 }}>Rs.{item.amount?.toLocaleString()}</span>
                  </div>
                ))}
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="GST Reports" key="gst">
          <Card style={{ ...CARD_STYLE, marginTop: 16 }} bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ color: '#C9A84C', fontWeight: 500 }}>Monthly GST Report</span>
              <Button icon={<DownloadOutlined />} style={{ color: '#C9A84C', borderColor: 'rgba(201,168,76,0.3)' }}>Download for CA</Button>
            </div>
            <div style={{ padding: '12px 16px', background: '#222222', borderRadius: 8, border: '0.5px solid rgba(201,168,76,0.15)' }}>
              <Row gutter={16}>
                <Col span={6}><div style={{ color: '#7A8A96', fontSize: 11 }}>Month</div><div style={{ color: '#D4DCE3' }}>May 2026</div></Col>
                <Col span={6}><div style={{ color: '#7A8A96', fontSize: 11 }}>Commission</div><div style={{ color: '#C9A84C' }}>Rs.{totalCommission?.toLocaleString()}</div></Col>
                <Col span={6}><div style={{ color: '#7A8A96', fontSize: 11 }}>GST (18%)</div><div style={{ color: '#FAAD14' }}>Rs.{Math.round(totalCommission * 0.18)?.toLocaleString()}</div></Col>
                <Col span={6}><div style={{ color: '#7A8A96', fontSize: 11 }}>Status</div><Tag style={{ background: 'rgba(250,173,20,0.1)', color: '#FAAD14', border: 'none', borderRadius: 20 }}>Current Month</Tag></Col>
              </Row>
            </div>
          </Card>
        </TabPane>
      </Tabs>

      {/* View Modal */}
      <Modal title={<span style={{ color: '#C9A84C' }}>Payout Details - {selected?.recipientName}</span>}
        open={viewModal} onCancel={() => setViewModal(false)} width={580}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            {selected?.status === 'pending' && (
              <Button icon={<CheckCircleOutlined />}
                style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                onClick={() => setConfirmModal(true)}>Process Payout</Button>
            )}
          </div>
        }>
        {selected && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Dealer">{selected.recipientName}</Descriptions.Item>
            <Descriptions.Item label="Bank Account">{selected.recipientBank}</Descriptions.Item>
            <Descriptions.Item label="Orders">{selected.orderCount} orders</Descriptions.Item>
            <Descriptions.Item label="Order Value">Rs.{selected.orderValue?.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Commission">Rs.{selected.commissionAmount?.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="GST on Commission">Rs.{selected.gstOnCommission}</Descriptions.Item>
            <Descriptions.Item label="Net Payout">
              <span style={{ color: '#52C41A', fontWeight: 600, fontSize: 16 }}>Rs.{selected.netPayout?.toLocaleString()}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">{selected.dueDate}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status={STATUS_STYLES[selected.status]?.badge} text={STATUS_STYLES[selected.status]?.label} />
            </Descriptions.Item>
            {selected.txnRef && (
              <Descriptions.Item label="TXN Ref" span={2}>
                <span style={{ color: '#1890FF', fontFamily: 'monospace' }}>{selected.txnRef}</span>
              </Descriptions.Item>
            )}
            {selected.paidAt && <Descriptions.Item label="Paid At" span={2}>{selected.paidAt}</Descriptions.Item>}
          </Descriptions>
        )}
      </Modal>

      {/* Confirm Modal */}
      <Modal title={<span style={{ color: '#52C41A' }}>Confirm Payout - {selected?.recipientName}</span>}
        open={confirmModal} onCancel={() => setConfirmModal(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setConfirmModal(false)}>Cancel</Button>
            <Button loading={actionLoading} icon={<CheckCircleOutlined />}
              style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
              onClick={handlePayout}>Confirm & Transfer</Button>
          </div>
        }>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 13, color: '#7A8A96', marginBottom: 8 }}>Transferring to</div>
          <div style={{ fontSize: 16, color: '#D4DCE3', fontWeight: 500, marginBottom: 4 }}>{selected?.recipientName}</div>
          <div style={{ fontSize: 13, color: '#7A8A96', marginBottom: 20 }}>{selected?.recipientBank}</div>
          <div style={{ fontSize: 32, fontWeight: 600, color: '#52C41A', marginBottom: 8 }}>
            Rs.{selected?.netPayout?.toLocaleString()}
          </div>
          <div style={{ fontSize: 12, color: '#4A4A47' }}>via Razorpay Payout API • NEFT/IMPS</div>
        </div>
      </Modal>
    </div>
  );
}